import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent
} from 'react';

import { askStream } from '@core/api/aoai.api';
import { Button, IconButton } from '@ui/Button';
import { Icon } from '@ui/Icon';

import './AssistantContainer.scss';

/**
 * The universal assistant pane - a port of `AssistantContainerComponent`.
 *
 * The stream is torn down through the unsubscribe callback `askStream` returns,
 * held in a ref so the pane can cancel an in-flight answer when the user starts
 * a new chat, presses stop, or the panel unmounts.
 *
 * Two layout decisions are deliberate, because this column is only ~438px wide:
 *
 *  - An assistant turn is plain text at full width, not a bubble with an
 *    avatar. Bubbles on both sides plus two avatar chips spend a third of the
 *    column on chrome, and the answer is what the user came for. Only the
 *    user's own turn is a bubble, which is enough to tell the two apart.
 *
 *  - The composer grows from one line rather than opening at three. A resting
 *    composer that fills a third of the pane reads as the main event; here it
 *    is the smaller half of a pane whose job is showing answers.
 */

type UniversalChatRole = 'user' | 'assistant';

interface UniversalChatMessage {
  id: number;
  role: UniversalChatRole;
  text: string;
  pending?: boolean;
}

/** ~6 lines. Past that the composer scrolls rather than eating the transcript. */
const MAX_COMPOSER_HEIGHT = 132;

/**
 * Openers for the empty pane, phrased as the work rather than as assistant
 * features - the first screen should tell an appraiser what this is for
 * instead of asking them to guess what it can do.
 */
const SUGGESTIONS = [
  'Summarise the scope of work on this activity',
  'Compare the rent comparables for this property',
  'What changed on this appraisal in the last week?'
];

export interface AssistantContainerProps {
  onClosePane: () => void;
}

export function AssistantContainer({ onClosePane }: AssistantContainerProps) {
  const [messages, setMessages] = useState<UniversalChatMessage[]>([]);
  const [draft, setDraft] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [threadId, setThreadId] = useState<string | undefined>(undefined);

  const nextMessageId = useRef(1);
  const unsubscribeRef = useRef<(() => void) | null>(null);
  const messagesEndRef = useRef<HTMLDivElement | null>(null);
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  // Cancel any stream still running when the pane goes away, so its callbacks
  // cannot set state on an unmounted component.
  useEffect(
    () => () => {
      unsubscribeRef.current?.();
    },
    []
  );

  // Keep the newest message in view as tokens arrive.
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ block: 'end' });
  }, [messages]);

  // The composer is one line at rest and grows with the draft.
  useLayoutEffect(() => {
    const el = composerRef.current;

    if (!el) {
      return;
    }

    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, MAX_COMPOSER_HEIGHT)}px`;
  }, [draft]);

  const updateAssistantMessage = useCallback((id: number, delta: string) => {
    setMessages((current) =>
      current.map((message) =>
        message.id === id ? { ...message, text: `${message.text}${delta}`, pending: false } : message
      )
    );
  }, []);

  const replaceAssistantMessage = useCallback((id: number, text: string) => {
    setMessages((current) =>
      current.map((message) => (message.id === id ? { ...message, text, pending: false } : message))
    );
  }, []);

  const finishAssistantMessage = useCallback((id: number) => {
    setIsStreaming(false);
    setMessages((current) =>
      current.map((message) => (message.id === id ? { ...message, pending: false } : message))
    );
  }, []);

  const sendMessage = useCallback(() => {
    const prompt = draft.trim();

    if (!prompt || isStreaming) {
      return;
    }

    const userMessageId = nextMessageId.current++;
    const assistantMessageId = nextMessageId.current++;

    setMessages((current) => [
      ...current,
      { id: userMessageId, role: 'user', text: prompt },
      { id: assistantMessageId, role: 'assistant', text: '', pending: true }
    ]);

    setDraft('');
    setErrorMessage('');
    setIsStreaming(true);

    unsubscribeRef.current?.();
    unsubscribeRef.current = askStream(
      { message: prompt, threadId },
      {
        next: (event) => {
          if (event.threadId) {
            setThreadId(event.threadId);
          }

          if (event.delta) {
            updateAssistantMessage(assistantMessageId, event.delta);
          }

          if (event.type === 'done') {
            finishAssistantMessage(assistantMessageId);
          }
        },
        error: () => {
          setIsStreaming(false);
          setErrorMessage(
            'The assistant could not respond. Check the AI service connection and try again.'
          );
          replaceAssistantMessage(
            assistantMessageId,
            'I could not reach the assistant service. Please try again once the connection is available.'
          );
        },
        complete: () => finishAssistantMessage(assistantMessageId)
      }
    );
  }, [
    draft,
    isStreaming,
    threadId,
    updateAssistantMessage,
    replaceAssistantMessage,
    finishAssistantMessage
  ]);

  /**
   * Stop, not a disabled spinner. A long answer the user no longer wants left
   * them watching an hourglass with nothing to press; aborting the fetch is
   * already what `askStream` returns, so the button just calls it.
   */
  const stopStreaming = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    setIsStreaming(false);

    // An assistant turn that never received a token has nothing to show, so it
    // goes rather than sitting in the transcript as an empty row.
    setMessages((current) =>
      current
        .filter((message) => message.role === 'user' || message.text)
        .map((message) => (message.pending ? { ...message, pending: false } : message))
    );
  }, []);

  const clearChat = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    setThreadId(undefined);
    setIsStreaming(false);
    setErrorMessage('');
    setMessages([]);
    setDraft('');
    composerRef.current?.focus();
  }, []);

  const applySuggestion = useCallback((suggestion: string) => {
    setDraft(suggestion);
    composerRef.current?.focus();
  }, []);

  const onComposerKeydown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // Enter sends, Shift+Enter makes a new line.
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      sendMessage();
    }
  };

  const isEmpty = messages.length === 0;

  return (
    <section className="assistant-pane" aria-label="Universal AI chat pane">
      <header className="assistant-pane__header">
        <h2 className="assistant-pane__title">
          <span className="assistant-pane__mark" aria-hidden="true">
            <Icon>auto_awesome</Icon>
          </span>
          Assistant
        </h2>

        <div className="assistant-pane__actions">
          <IconButton
            size="sm"
            label="Start a new chat"
            onClick={clearChat}
            disabled={isEmpty && !draft}
          >
            <Icon>edit_square</Icon>
          </IconButton>
          <IconButton size="sm" label="Close assistant" onClick={onClosePane}>
            <Icon>close</Icon>
          </IconButton>
        </div>
      </header>

      <div
        className={['assistant-pane__messages', isEmpty ? 'assistant-pane__messages--empty' : null]
          .filter(Boolean)
          .join(' ')}
        role="log"
        aria-live="polite"
      >
        {isEmpty && (
          <div className="assistant-empty">
            <span className="assistant-empty__mark" aria-hidden="true">
              <Icon>auto_awesome</Icon>
            </span>

            <p className="assistant-empty__title">How can I help?</p>
            <p className="assistant-empty__copy">
              Ask about an assignment, a comparable set or a report section.
            </p>

            <ul className="assistant-empty__suggestions">
              {SUGGESTIONS.map((suggestion) => (
                <li key={suggestion}>
                  <Button
                    variant="secondary"
                    size="sm"
                    block
                    className="assistant-suggestion"
                    trailingIcon={<Icon>arrow_forward</Icon>}
                    onClick={() => applySuggestion(suggestion)}
                  >
                    {suggestion}
                  </Button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {messages.map((message) => (
          <article
            key={message.id}
            className={[
              'assistant-message',
              message.role === 'user' ? 'assistant-message--user' : 'assistant-message--assistant'
            ].join(' ')}
          >
            {message.text && <p className="assistant-message__text">{message.text}</p>}

            {message.pending && !message.text && (
              <div className="typing-indicator" role="status" aria-label="Thinking">
                <span />
                <span />
                <span />
              </div>
            )}
          </article>
        ))}

        <div ref={messagesEndRef} />
      </div>

      {errorMessage && (
        <p className="assistant-pane__error" role="alert">
          <Icon>error_outline</Icon>
          {errorMessage}
        </p>
      )}

      <form
        className="assistant-composer"
        onSubmit={(event) => {
          event.preventDefault();
          sendMessage();
        }}
      >
        <div className="assistant-composer__shell">
          <textarea
            ref={composerRef}
            name="assistantPrompt"
            rows={1}
            placeholder="Ask me anything"
            aria-label="Ask the universal assistant"
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={onComposerKeydown}
          />

          <div className="assistant-composer__tools">
            <IconButton size="sm" label="Add attachment">
              <Icon>add</Icon>
            </IconButton>

            <Button
              variant="ghost"
              size="sm"
              className="assistant-composer__model"
              trailingIcon={<Icon>expand_more</Icon>}
            >
              Auto
            </Button>

            <span className="assistant-composer__spacer" />

            {isStreaming ? (
              <IconButton
                size="sm"
                className="assistant-composer__send"
                label="Stop generating"
                onClick={stopStreaming}
              >
                <Icon>stop</Icon>
              </IconButton>
            ) : (
              <IconButton
                size="sm"
                type="submit"
                className="assistant-composer__send"
                label="Send message"
                disabled={!draft.trim()}
              >
                <Icon>arrow_upward</Icon>
              </IconButton>
            )}
          </div>
        </div>
      </form>
    </section>
  );
}

export default AssistantContainer;
